import { createContext, useContext, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'dutilyne_portal_theme';
const Context = createContext({ theme: 'light', toggleTheme: () => {} });

const getInitialTheme = () => {
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
  if (saved === 'light' || saved === 'dark') return saved;
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
};

/**
 * Light/dark preference for the patient portal, independent from any staff
 * theme toggle. Applies a `dark` class to <html> while the portal is
 * mounted, and restores whatever class was present before (e.g. a staff
 * dark preference, if this tab was ever on a staff screen) once the portal
 * unmounts.
 */
export const PortalThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(getInitialTheme);
  const priorDarkRef = useRef(null);

  // Mount/unmount: remember and restore whatever was on <html> before.
  useEffect(() => {
    const root = document.documentElement;
    priorDarkRef.current = root.classList.contains('dark');
    return () => { root.classList.toggle('dark', priorDarkRef.current); };
  }, []);

  // Apply the portal's own preference while mounted.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  return <Context.Provider value={{ theme, toggleTheme }}>{children}</Context.Provider>;
};

export const usePortalTheme = () => useContext(Context);
